/**
 * Vertical protocol workspace
 */
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { OaWorkspaceShell } from '../components/OaWorkspaceShell';
import { fetchProtocol, fetchPathways } from '../api/client';
import { trackOncoAgentPageView } from '../utils/analytics';
import { VERTICALS, VERTICAL_BY_ID } from '../verticals/catalog';
import { oaAppPath } from '../app/paths';

export function OncoAgentVertical(): React.ReactElement {
  const { verticalId = 'oral' } = useParams();
  const vid = (verticalId || 'oral').toLowerCase();
  const meta = VERTICAL_BY_ID[vid] || VERTICAL_BY_ID.oral;
  const [protocol, setProtocol] = useState<any>(null);
  const [pathways, setPathways] = useState<any[]>([]);
  const [packMeta, setPackMeta] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    trackOncoAgentPageView(oaAppPath(`/pathways/${vid}`), meta.title);
    document.title = `${meta.title} · OncoAgent`;
    let cancelled = false;
    (async () => {
      try {
        const [p, pw] = await Promise.all([fetchProtocol(vid), fetchPathways(vid)]);
        if (cancelled) return;
        setProtocol(p.protocol);
        setPathways(pw.pathways || []);
        setPackMeta(pw.meta);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load protocol');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [vid, meta.title]);

  return (
    <OaWorkspaceShell kicker={`Pathway · ${vid}`} title={meta.title} subtitle={meta.blurb}>
      {error ? <div className="oa-ws-error">{error}</div> : null}

      <div className="oa-ws-chip-row" style={{ marginBottom: '1.25rem' }}>
        {VERTICALS.map((v) => (
          <Link
            key={v.id}
            to={oaAppPath(`/pathways/${v.id}`)}
            className={`oa-ws-chip ${vid === v.id ? 'on' : ''}`}
          >
            {v.name}
          </Link>
        ))}
      </div>

      <div className="oa-ws-grid cols-2">
        <div className="oa-ws-panel">
          <h2>Protocol steps</h2>
          <p className="oa-ws-muted">{protocol?.specialty || meta.specialty}</p>
          <ul className="oa-ws-list">
            {(protocol?.steps || []).map((s: any) => (
              <li key={s.id}>
                <strong>
                  {s.title}
                  {s.required ? ' · required' : ''}
                </strong>
                <div className="oa-ws-muted">{s.detail}</div>
                {s.cds_prompt ? (
                  <div className="oa-ws-muted" style={{ marginTop: 6 }}>
                    CDS: {s.cds_prompt}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
          <div className="oa-ws-actions">
            <Link className="oa-ws-btn oa-ws-btn-primary" to={`${oaAppPath('/refer')}?vertical=${vid}`}>
              Start {meta.name} referral
            </Link>
          </div>
        </div>

        <div className="oa-ws-panel">
          <h2>Matched pathway pack</h2>
          <p className="oa-ws-muted">
            {packMeta?.license || 'ByOnco pathway pack'} ·{' '}
            {packMeta?.nccn_licensed
              ? 'NCCN licensed'
              : 'Not NCCN-licensed. License-ready schema.'}
          </p>
          <ul className="oa-ws-list">
            {pathways.map((p) => (
              <li key={p.pathway_id}>
                <strong>{p.title}</strong>
                <div className="oa-ws-muted">
                  Urgency {p.urgency_default} · {p.specialty}
                </div>
                <div className="oa-ws-muted" style={{ marginTop: 6 }}>
                  Triggers: {(p.triggers || []).join(', ')}
                </div>
              </li>
            ))}
            {!pathways.length ? (
              <li className="oa-ws-muted">No vertical-specific pathways listed yet. Capture still routes.</li>
            ) : null}
          </ul>
          {packMeta?.disclaimer ? (
            <p className="oa-ws-muted" style={{ marginTop: '1rem' }}>
              {packMeta.disclaimer}
            </p>
          ) : null}
        </div>
      </div>

      <div className="oa-ws-panel">
        <h3>Capture fields</h3>
        <div className="oa-ws-chip-row">
          {(protocol?.capture_fields || []).map((f: string) => (
            <span key={f} className="oa-ws-chip on">
              {f}
            </span>
          ))}
        </div>
      </div>
    </OaWorkspaceShell>
  );
}

export default OncoAgentVertical;
